import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Users, Eye, Monitor, Smartphone, Globe2, MapPin } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { API } from "../../lib/store";

const PERIODS = [["today", "Today"], ["7d", "7 Days"], ["30d", "30 Days"]];

function RankList({ title, icon, items, unit, testid }) {
  const max = Math.max(1, ...items.map((i) => i.visitors));
  return (
    <div className="bg-white border border-[#eee6d6] p-5" data-testid={testid}>
      <h4 className="font-heading text-lg text-[#171310] flex items-center gap-2">{icon} {title}</h4>
      {items.length === 0 ? (
        <p className="text-xs text-[#8f8571] mt-3">No data yet</p>
      ) : (
        <div className="space-y-2.5 mt-4">
          {items.map((it) => (
            <div key={it.name}>
              <div className="flex justify-between text-xs text-[#171310]">
                <span className="truncate pr-2">{it.name}</span>
                <span className="text-[#8f8571] shrink-0">{it.visitors} {unit}</span>
              </div>
              <div className="h-1.5 bg-[#faf7f0] mt-1">
                <div className="h-full bg-[#a8842c]" style={{ width: `${(it.visitors / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TrafficTab({ auth }) {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/traffic`, { ...auth, params: { period } })
      .then((r) => setData(r.data))
      .catch((e) => console.warn("Traffic fetch failed:", e?.message));
  }, [auth, period]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return <p className="text-sm text-[#8f8571] py-8">Loading traffic...</p>;

  const mobile = data.devices?.mobile || 0;
  const desktop = data.devices?.desktop || 0;
  const devTotal = Math.max(1, mobile + desktop);

  return (
    <div className="space-y-6" data-testid="traffic-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-[#8f8571]">Your own visitor counter — anonymous, first-party, refreshes every 20 seconds. Counting started the moment this version went live.</p>
        <div className="flex gap-1">
          {PERIODS.map(([val, label]) => (
            <button key={val} onClick={() => setPeriod(val)}
              className={`text-xs uppercase tracking-[0.1em] px-4 py-2 border transition-colors duration-200 ${period === val ? "bg-[#171310] text-[#e9d9ac] border-[#171310]" : "bg-white text-[#171310] border-[#d8cdb5] hover:border-[#171310]"}`}
              data-testid={`traffic-period-${val}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#171310] p-5" data-testid="traffic-live-card">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#c9bd9d]">Live right now</p>
          </div>
          <p className="font-heading text-4xl text-[#e9d9ac] mt-2">{data.live}</p>
          <p className="text-[10px] text-[#8f8571] mt-1">active in last 5 min</p>
        </div>
        <div className="bg-white border border-[#eee6d6] p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8f8571] flex items-center gap-1.5"><Users size={12} /> Visitors</p>
          <p className="font-heading text-4xl text-[#171310] mt-2" data-testid="traffic-visitors">{data.visitors}</p>
        </div>
        <div className="bg-white border border-[#eee6d6] p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8f8571] flex items-center gap-1.5"><Eye size={12} /> Page views</p>
          <p className="font-heading text-4xl text-[#171310] mt-2" data-testid="traffic-pageviews">{data.pageviews}</p>
        </div>
        <div className="bg-white border border-[#eee6d6] p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8f8571]">Devices</p>
          <div className="flex items-center gap-3 mt-3 text-sm text-[#171310]">
            <span className="flex items-center gap-1.5"><Smartphone size={14} className="text-[#a8842c]" /> {Math.round((mobile / devTotal) * 100)}%</span>
            <span className="flex items-center gap-1.5"><Monitor size={14} className="text-[#a8842c]" /> {Math.round((desktop / devTotal) * 100)}%</span>
          </div>
          <p className="text-[10px] text-[#8f8571] mt-2">mobile / desktop</p>
        </div>
      </div>

      <div className="bg-white border border-[#eee6d6] p-5">
        <h4 className="font-heading text-lg text-[#171310]">Visitors per day</h4>
        <div className="h-56 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#8f8571" }} axisLine={{ stroke: "#eee6d6" }} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "#8f8571" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#faf7f0" }} contentStyle={{ borderRadius: 0, border: "1px solid #eee6d6", fontSize: 12 }} />
              <Bar dataKey="visitors" fill="#a8842c" maxBarSize={34} />
              <Bar dataKey="views" fill="#e3d3a5" maxBarSize={34} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <RankList title="Where visitors come from" icon={<Globe2 size={16} className="text-[#a8842c]" />} items={data.sources} unit="visitors" testid="traffic-sources" />
        <RankList title="Where visitors are located" icon={<MapPin size={16} className="text-[#a8842c]" />} items={data.locations} unit="visitors" testid="traffic-locations" />
      </div>

      <div className="bg-white border border-[#eee6d6] p-5" data-testid="traffic-pages">
        <h4 className="font-heading text-lg text-[#171310]">Most viewed pages</h4>
        {data.pages.length === 0 ? (
          <p className="text-xs text-[#8f8571] mt-3">No data yet</p>
        ) : (
          <div className="divide-y divide-[#f2ecdd] mt-2">
            {data.pages.map((p) => (
              <div key={p.path} className="flex justify-between items-center py-2 text-xs">
                <span className="text-[#171310] truncate pr-3">{p.path}</span>
                <span className="text-[#8f8571] shrink-0">{p.views} views</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
